import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { faRotateRight, faBellSlash, faBell, faPen, faTrash } from '@fortawesome/free-solid-svg-icons';
import Sidebar from '../components/layout/Sidebar';
import HostingNav from '../components/layout/HostingNav';
import PageHeader from '../components/PageHeader';
import UptimeStatusBadge from '../components/uptime/UptimeStatusBadge';
import UptimeSparkline from '../components/uptime/UptimeSparkline';
import SiteSidepanel from '../components/uptime/SiteSidepanel';
import {
  Button,
  Badge,
  StatCard,
  Tabs,
  Table,
  KebabMenu,
  Icon,
  Popover,
} from '../components/ui';
import type { TableColumn } from '../components/ui';
import { useUser } from '../context/UserContext';
import { supabase } from '../lib/supabase';
import {
  UptimeCheck,
  MonitoredSite,
  calculateAvailability,
  calculateApdex,
  calculateDowntime,
  calculateAvgResponseTime,
  getLast24HoursChecks,
} from '../lib/uptime';

interface MonitoredSiteWithUptime extends MonitoredSite {
  checks: UptimeCheck[];
  latestCheck: UptimeCheck | null;
  availability: number;
  downtime: { minutes: number; percentage: number };
  apdex: number;
  avgResponseTime: number;
}

type FilterTab = 'all' | 'up' | 'down' | 'paused';

const TAB_LABELS: Array<{ label: string; value: FilterTab }> = [
  { label: 'All', value: 'all' },
  { label: 'Up', value: 'up' },
  { label: 'Down', value: 'down' },
];

const SNOOZE_HOURS = [1, 2, 4, 8];

const normalizeRole = (role?: string | null) => role?.trim?.().toLowerCase?.();

export default function Uptime() {
  const { user, isUserLoaded } = useUser();
  const navigate = useNavigate();
  const [sites, setSites] = useState<MonitoredSiteWithUptime[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [isSidepanelOpen, setIsSidepanelOpen] = useState(false);
  const [editingSite, setEditingSite] = useState<MonitoredSiteWithUptime | null>(null);
  const [showSnoozeAll, setShowSnoozeAll] = useState(false);
  const [isSnoozing, setIsSnoozing] = useState(false);
  const snoozeAllRef = useRef<HTMLSpanElement>(null);

  // Check permissions
  const userRole = normalizeRole(user?.role);
  const hasAccess = userRole === 'admin' || userRole === 'developer' || userRole === 'agency_owner';

  const fetchUptimeData = useCallback(async () => {
    try {
      // Fetch all websites
      const { data: sitesData, error: sitesError } = await supabase
        .from('monitored_sites')
        .select('id, name, url, is_active, alerts_snoozed_until, alert_recipients')
        .order('name', { ascending: true });

      if (sitesError) throw sitesError;

      // Fetch uptime checks for last 24 hours. Filter by site ids so the
      // (site_id, checked_at) index is used — a date-only filter seq-scans
      // millions of rows and times out (500) as the table grows.
      const yesterday = new Date();
      yesterday.setHours(yesterday.getHours() - 24);

      const siteIds = (sitesData || []).map((w: MonitoredSite) => w.id);
      let checksData: UptimeCheck[] = [];
      if (siteIds.length > 0) {
        const { data, error: checksError } = await supabase
          .from('uptime_checks')
          .select('id, site_id, checked_at, status_code, response_time_ms, is_up, error_message')
          .in('site_id', siteIds)
          .gte('checked_at', yesterday.toISOString())
          .order('checked_at', { ascending: false });

        if (checksError) throw checksError;
        checksData = (data as UptimeCheck[]) || [];
      }

      // Group checks by website
      const checksByMonitoredSite = new Map<string, UptimeCheck[]>();
      (checksData || []).forEach((check: UptimeCheck) => {
        const existing = checksByMonitoredSite.get(check.site_id) || [];
        existing.push(check);
        checksByMonitoredSite.set(check.site_id, existing);
      });

      // Build website data with uptime stats
      const sitesWithUptime: MonitoredSiteWithUptime[] = (sitesData || []).map((website: MonitoredSite) => {
        const checks = checksByMonitoredSite.get(website.id) || [];
        const latestCheck = checks.length > 0 ? checks[0] : null;
        const last24h = getLast24HoursChecks(checks);

        return {
          ...website,
          checks: last24h,
          latestCheck,
          availability: calculateAvailability(last24h),
          downtime: calculateDowntime(last24h),
          apdex: calculateApdex(last24h),
          avgResponseTime: calculateAvgResponseTime(last24h),
        };
      });

      setSites(sitesWithUptime);
      setLoadError(null);
    } catch (error) {
      console.error('Error fetching uptime data:', error);
      setLoadError('Could not load sites. Try Refresh — if it keeps failing, check the console for details.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    // Wait for isUserLoaded: it flips true only after the Supabase session is
    // restored, so fetching earlier silently returns zero rows under RLS.
    if (isUserLoaded && hasAccess) {
      fetchUptimeData();
    }
  }, [isUserLoaded, hasAccess, fetchUptimeData]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await fetchUptimeData();
    setIsRefreshing(false);
  };

  const isSnoozed = (site: MonitoredSite) =>
    site.alerts_snoozed_until && new Date(site.alerts_snoozed_until) > new Date();

  const formatSnoozeExpiry = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const formatCheckedAt = (iso: string) => {
    const d = new Date(iso);
    return d.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  };

  const anySnoozed = sites.some(s => isSnoozed(s));

  const handleSnoozeAll = async (hours: number) => {
    setIsSnoozing(true);
    const until = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    const { error } = await supabase
      .from('monitored_sites')
      .update({ alerts_snoozed_until: until })
      .eq('is_active', true);
    if (!error) await fetchUptimeData();
    setIsSnoozing(false);
    setShowSnoozeAll(false);
  };

  const handleUnsnoozeAll = async () => {
    setIsSnoozing(true);
    const { error } = await supabase
      .from('monitored_sites')
      .update({ alerts_snoozed_until: null })
      .eq('is_active', true);
    if (!error) await fetchUptimeData();
    setIsSnoozing(false);
  };

  const handleSnoozeSite = async (site: MonitoredSiteWithUptime, hours: number) => {
    const until = new Date(Date.now() + hours * 60 * 60 * 1000).toISOString();
    const { error } = await supabase
      .from('monitored_sites')
      .update({ alerts_snoozed_until: until })
      .eq('id', site.id);
    if (!error) await fetchUptimeData();
  };

  const handleUnsnoozeSite = async (site: MonitoredSiteWithUptime) => {
    const { error } = await supabase
      .from('monitored_sites')
      .update({ alerts_snoozed_until: null })
      .eq('id', site.id);
    if (!error) await fetchUptimeData();
  };

  const handleDeleteSite = async (site: MonitoredSiteWithUptime) => {
    const confirmed = window.confirm(
      `Delete ${site.name || site.url} and all its uptime history? This cannot be undone.`
    );
    if (!confirmed) return;

    try {
      // Delete uptime checks first (FK constraint)
      const { error: checksError } = await supabase
        .from('uptime_checks')
        .delete()
        .eq('site_id', site.id);
      if (checksError) throw checksError;

      // Delete incidents
      await supabase
        .from('uptime_incidents')
        .delete()
        .eq('site_id', site.id);

      // Delete the site
      const { error: deleteError } = await supabase
        .from('monitored_sites')
        .delete()
        .eq('id', site.id);
      if (deleteError) throw deleteError;

      await fetchUptimeData();
    } catch (error) {
      console.error('Error deleting site:', error);
    }
  };

  // Filter websites
  const filteredMonitoredSites = useMemo(() => {
    let filtered = sites;

    // Search filter
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(
        w =>
          w.url.toLowerCase().includes(query) ||
          w.name?.toLowerCase().includes(query)
      );
    }

    // Tab filter
    if (activeTab === 'up') {
      filtered = filtered.filter(w => w.latestCheck?.is_up === true);
    } else if (activeTab === 'down') {
      filtered = filtered.filter(w => w.latestCheck?.is_up === false);
    }
    // 'paused' would filter by a monitoring_enabled field if we add it later

    return filtered;
  }, [sites, searchQuery, activeTab]);

  // Summary stats
  const stats = useMemo(() => {
    const total = sites.length;
    const up = sites.filter(w => w.latestCheck?.is_up === true).length;
    const down = sites.filter(w => w.latestCheck?.is_up === false).length;
    const downSite = sites.find(w => w.latestCheck?.is_up === false);
    const avgUptime =
      total > 0
        ? (sites.reduce((sum, w) => sum + w.availability, 0) / total).toFixed(2)
        : null;
    const latestHealthy = sites
      .filter(w => w.latestCheck?.is_up === true && w.latestCheck.response_time_ms !== null)
      .map(w => w.latestCheck!.response_time_ms!);
    const avgResponse =
      latestHealthy.length > 0
        ? Math.round(latestHealthy.reduce((a, b) => a + b, 0) / latestHealthy.length)
        : null;
    return { total, up, down, downSite, avgUptime, avgResponse };
  }, [sites]);

  const activeTabLabel = TAB_LABELS.find(t => t.value === activeTab)?.label ?? 'All';

  const columns: TableColumn<MonitoredSiteWithUptime>[] = [
    {
      key: 'status',
      label: 'Status',
      render: (_v, row) => <UptimeStatusBadge isUp={row.latestCheck?.is_up ?? null} />,
    },
    {
      key: 'name',
      label: 'Site',
      render: (_v, row) => (
        <span>
          <span
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontWeight: 500,
              color: 'var(--text-body)',
            }}
          >
            {row.name || 'Untitled'}
            {isSnoozed(row) && (
              <Badge tone="idle">
                Snoozed · until {formatSnoozeExpiry(row.alerts_snoozed_until!)}
              </Badge>
            )}
            <span className="row-chevron">
              <Icon name="chevron-right" size={12} />
            </span>
          </span>
          <span
            className="t-num"
            style={{ display: 'block', fontSize: 'var(--text-xs)', color: 'var(--text-faint)' }}
          >
            {row.url}
          </span>
        </span>
      ),
    },
    {
      key: 'availability',
      label: 'Uptime · 24h',
      align: 'right',
      mono: true,
      render: (_v, row) => (
        <span style={{ color: row.availability < 99 ? 'var(--status-warn)' : 'var(--text-body)' }}>
          {row.availability.toFixed(2)}%
        </span>
      ),
    },
    {
      key: 'checks',
      label: 'Response · 24 checks',
      render: (_v, row) => <UptimeSparkline checks={row.checks} />,
    },
    {
      key: 'latest',
      label: 'Latest',
      align: 'right',
      mono: true,
      render: (_v, row) => {
        if (!row.latestCheck) return <span style={{ color: 'var(--text-faint)' }}>—</span>;
        if (!row.latestCheck.is_up) return <span style={{ color: 'var(--status-down)' }}>failed</span>;
        return row.latestCheck.response_time_ms !== null
          ? `${row.latestCheck.response_time_ms}ms`
          : '—';
      },
    },
    {
      key: 'checked',
      label: 'Checked',
      align: 'right',
      mono: true,
      render: (_v, row) =>
        row.latestCheck ? (
          formatCheckedAt(row.latestCheck.checked_at)
        ) : (
          <span style={{ color: 'var(--text-faint)' }}>—</span>
        ),
    },
    {
      key: 'actions',
      label: '',
      align: 'right',
      render: (_v, row) => siteKebab(row),
    },
  ];

  // A plain function, not a component: a component defined in render would
  // remount (and close its open menu) on every re-render.
  const siteKebab = (row: MonitoredSiteWithUptime) => (
    <KebabMenu
      label="Site actions"
      actions={[
        { label: 'Check Now', icon: faRotateRight, onClick: () => handleRefresh() },
        isSnoozed(row)
          ? { label: 'Unsnooze', icon: faBell, onClick: () => handleUnsnoozeSite(row) }
          : { label: 'Snooze 2 Hours', icon: faBellSlash, onClick: () => handleSnoozeSite(row, 2) },
        {
          label: 'Edit Site',
          icon: faPen,
          onClick: () => {
            setEditingSite(row);
            setIsSidepanelOpen(true);
          },
        },
        { label: 'Delete', icon: faTrash, danger: true, onClick: () => handleDeleteSite(row) },
      ]}
    />
  );

  const latestText = (row: MonitoredSiteWithUptime) => {
    if (!row.latestCheck) return null;
    if (!row.latestCheck.is_up) return 'failed';
    return row.latestCheck.response_time_ms !== null ? `${row.latestCheck.response_time_ms}ms` : null;
  };

  if (!isUserLoaded) return null;

  if (!hasAccess) {
    return <Navigate to="/clients" replace />;
  }

  return (
    <div className="min-h-screen">
      <Sidebar />

      <main className="px-4 md:px-8 pb-8 md:ml-[72px]">
        <PageHeader
          title="Uptime"
          count={sites.length}
          searchPlaceholder="Search sites..."
          searchValue={searchQuery}
          onSearchChange={setSearchQuery}
          actions={
            <span style={{ display: 'inline-flex', flexWrap: 'wrap', gap: '8px', marginLeft: '6px' }}>
              <Button
                size="sm"
                icon="plus"
                onClick={() => {
                  setEditingSite(null);
                  setIsSidepanelOpen(true);
                }}
              >
                Add Site
              </Button>

              {/* Snooze All / Unsnooze All */}
              {anySnoozed ? (
                <Button
                  size="sm"
                  variant="secondary"
                  icon="bell"
                  disabled={isSnoozing}
                  onClick={handleUnsnoozeAll}
                >
                  Unsnooze All
                </Button>
              ) : (
                <span ref={snoozeAllRef} style={{ position: 'relative', display: 'inline-flex' }}>
                  <Button
                    size="sm"
                    variant="secondary"
                    icon="bell-off"
                    disabled={isSnoozing}
                    onClick={() => setShowSnoozeAll(prev => !prev)}
                  >
                    Snooze All
                  </Button>
                  <Popover
                    open={showSnoozeAll}
                    anchorEl={snoozeAllRef.current}
                    onClose={() => setShowSnoozeAll(false)}
                    align="right"
                    minWidth={176}
                    role="menu"
                  >
                      <div
                        className="t-eyebrow"
                        style={{
                          padding: '7px 10px 5px',
                          borderBottom: '1px solid var(--border-hairline)',
                          marginBottom: '3px',
                        }}
                      >
                        Snooze alerts for...
                      </div>
                      {SNOOZE_HOURS.map(h => (
                        <button
                          key={h}
                          className="hover-wash pressable"
                          onClick={() => handleSnoozeAll(h)}
                          style={{
                            display: 'block',
                            width: '100%',
                            textAlign: 'left',
                            padding: '7px 10px',
                            border: 'none',
                            background: 'transparent',
                            borderRadius: 'var(--radius-sm)',
                            cursor: 'pointer',
                            fontFamily: 'var(--font-ui)',
                            fontSize: 'var(--text-sm)',
                            color: 'var(--text-body)',
                          }}
                        >
                          {h} hour{h > 1 ? 's' : ''}
                        </button>
                      ))}
                  </Popover>
                </span>
              )}

              <span className="hover-spin" style={{ display: 'inline-flex' }}>
                <Button
                  size="sm"
                  variant="secondary"
                  icon="refresh-cw"
                  disabled={isRefreshing}
                  onClick={handleRefresh}
                >
                  {isRefreshing ? 'Checking...' : 'Refresh'}
                </Button>
              </span>
            </span>
          }
        />

        <HostingNav />

        {/* KPI strip */}
        <div
          className="ck-stagger grid grid-cols-2 lg:grid-cols-4"
          style={{ gap: 'var(--space-4)', marginBottom: 'var(--space-6)' }}
        >
          <StatCard
            label="Monitors"
            value={`${stats.up}/${stats.total}`}
            hint="responding"
          />
          <StatCard
            label="Down Now"
            value={String(stats.down)}
            delta={stats.down > 0 ? 'alerting' : undefined}
            deltaTone={stats.down > 0 ? 'down' : undefined}
            hint={stats.down > 0 ? stats.downSite?.url : 'all clear'}
          />
          <StatCard
            label="Avg Uptime · 24h"
            value={stats.avgUptime !== null ? `${stats.avgUptime}%` : '—'}
            hint="across all monitors"
          />
          <StatCard
            label="Avg Response"
            value={stats.avgResponse !== null ? `${stats.avgResponse}ms` : '—'}
            hint="latest healthy checks"
          />
        </div>

        {/* Tabs */}
        <div style={{ marginBottom: 'var(--space-5)' }}>
          <Tabs
            tabs={TAB_LABELS.map(t => t.label)}
            active={activeTabLabel}
            onChange={label => {
              const tab = TAB_LABELS.find(t => t.label === label);
              if (tab) setActiveTab(tab.value);
            }}
          />
        </div>

        {/* Table */}
        {isLoading ? (
          <div className="flex items-center justify-center py-20">
            <div
              className="w-7 h-7 rounded-full animate-spin"
              style={{ border: '2px solid var(--accent)', borderTopColor: 'transparent' }}
            />
          </div>
        ) : loadError ? (
          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              padding: '48px 0',
              textAlign: 'center',
              color: 'var(--status-down)',
              fontSize: 'var(--text-sm)',
            }}
          >
            {loadError}
          </div>
        ) : filteredMonitoredSites.length === 0 ? (
          <div
            style={{
              background: 'var(--surface-card)',
              border: '1px solid var(--border-hairline)',
              borderRadius: 'var(--radius-lg)',
              padding: '48px 0',
              textAlign: 'center',
              color: 'var(--text-faint)',
              fontSize: 'var(--text-sm)',
            }}
          >
            No sites match
          </div>
        ) : (
          <>
            {/* Phones: one compact row per site instead of a seven-column
                table that only shows Status and Site */}
            <div
              className="md:hidden"
              style={{
                background: 'var(--surface-card)',
                border: '1px solid var(--border-hairline)',
                borderRadius: 'var(--radius-lg)',
                boxShadow: 'var(--shadow-rest)',
                overflow: 'hidden',
              }}
            >
              {filteredMonitoredSites.map((row, i) => {
                const latest = latestText(row);
                return (
                  <div
                    key={row.id}
                    role="link"
                    tabIndex={0}
                    className="hover-wash"
                    onClick={() => navigate(`/uptime/${row.id}`)}
                    onKeyDown={e => {
                      if (e.key === 'Enter') navigate(`/uptime/${row.id}`);
                    }}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '10px',
                      padding: '12px 8px 12px 14px',
                      borderTop: i ? '1px solid var(--border-hairline)' : 'none',
                      cursor: 'pointer',
                    }}
                  >
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '4px 8px' }}>
                        <span style={{ fontWeight: 500, color: 'var(--text-body)', overflowWrap: 'anywhere' }}>
                          {row.name || 'Untitled'}
                        </span>
                        {isSnoozed(row) && (
                          <Badge tone="idle">Snoozed · {formatSnoozeExpiry(row.alerts_snoozed_until!)}</Badge>
                        )}
                      </div>
                      <div
                        className="t-num"
                        style={{
                          fontSize: 'var(--text-xs)',
                          color: 'var(--text-faint)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.url.replace(/^https?:\/\//, '').replace(/\/$/, '')}
                      </div>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: '4px', flexShrink: 0 }}>
                      <UptimeStatusBadge isUp={row.latestCheck?.is_up ?? null} />
                      <span className="t-num" style={{ fontSize: 'var(--text-xs)', color: 'var(--text-faint)', whiteSpace: 'nowrap' }}>
                        <span style={{ color: row.availability < 99 ? 'var(--status-warn)' : 'var(--text-muted)' }}>
                          {row.availability.toFixed(2)}%
                        </span>
                        {latest ? (
                          <span style={{ color: latest === 'failed' ? 'var(--status-down)' : undefined }}> · {latest}</span>
                        ) : null}
                      </span>
                    </div>
                    {siteKebab(row)}
                  </div>
                );
              })}
            </div>
            <div className="hidden md:block overflow-x-auto">
              <Table
                columns={columns}
                rows={filteredMonitoredSites}
                onRowClick={row => navigate(`/uptime/${row.id}`)}
              />
            </div>
          </>
        )}
      </main>

      {/* Add/Edit Site Sidepanel */}
      <SiteSidepanel
        isOpen={isSidepanelOpen}
        onClose={() => {
          setIsSidepanelOpen(false);
          setEditingSite(null);
        }}
        onSiteChanged={fetchUptimeData}
        site={editingSite}
      />
    </div>
  );
}
